import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import AdminEmptyState from './AdminEmptyState'
import AdminListingDrawer from './AdminListingDrawer'
import AdminPagination from './AdminPagination'
import AdminSkeleton from './AdminSkeleton'
import { useAdminUi } from './AdminUiProvider'
import type { AdminListingRow } from './adminTypes'
import { exportCsv, formatWhen } from './adminUtils'

type Props = {
  token: string
  initialFilter?: '' | 'above_ai' | 'no_photos' | 'flagged' | 'hidden'
  initialOpenId?: number | null
  onOpenUser?: (userId: number) => void
}

type StatusFilter = 'active' | 'sold' | 'all'
type ListingFilter = '' | 'above_ai' | 'no_photos' | 'flagged' | 'hidden'

export default function AdminListingsPanel({
  token,
  initialFilter = '',
  initialOpenId = null,
  onOpenUser,
}: Props) {
  const { t, i18n } = useTranslation()
  const { toast } = useAdminUi()
  const [items, setItems] = useState<AdminListingRow[]>([])
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<StatusFilter>('active')
  const [filter, setFilter] = useState<ListingFilter>(initialFilter)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(initialOpenId)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const pageSize = 50

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    adminAPI
      .listListings(token, {
        q: q.trim() || undefined,
        status,
        filter: filter || undefined,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      })
      .then((res) => {
        setItems((res.data?.items as AdminListingRow[]) ?? [])
        setTotal(Number(res.data?.total ?? 0))
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false))
  }, [token, q, status, filter, page])

  useEffect(() => {
    const tmr = window.setTimeout(load, q ? 250 : 0)
    return () => window.clearTimeout(tmr)
  }, [load, q])

  useEffect(() => {
    setFilter(initialFilter)
  }, [initialFilter])

  useEffect(() => {
    if (initialOpenId) setSelectedId(initialOpenId)
  }, [initialOpenId])

  useEffect(() => {
    setPage(1)
    setSelectedIds([])
  }, [q, status, filter])

  function toggleSelected(id: number) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  function bulkPatch(body: { is_hidden?: boolean; is_flagged_suspicious?: boolean }) {
    if (!selectedIds.length) return
    adminAPI
      .bulkUpdateListings(token, { listing_ids: selectedIds, ...body })
      .then(() => {
        toast.success(t('admin.toast.bulkUpdated', { count: selectedIds.length }))
        setSelectedIds([])
        load()
      })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
  }

  function exportRows() {
    exportCsv(
      'listings.csv',
      ['id', 'title', 'price', 'owner', 'photos', 'hidden', 'flagged', 'created_at'],
      items.map((row) => [
        row.id,
        row.title,
        row.price,
        row.owner_email ?? '',
        row.photos_count,
        row.is_hidden ? 'yes' : 'no',
        row.is_flagged_suspicious ? 'yes' : 'no',
        row.created_at ?? '',
      ]),
    )
  }

  function handleUpdated(updated: AdminListingRow) {
    setItems((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
  }

  return (
    <div className="vc-admin-panel vc-admin-panel--card">
      <header className="vc-admin-panel__head">
        <div>
          <h2>{t('admin.nav.listings')}</h2>
          <p className="vc-admin-panel__hint">{t('admin.listingsIntro')}</p>
        </div>
        <div className="vc-admin-panel__actions">
          <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={exportRows}>
            {t('admin.exportCsv')}
          </button>
          <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={load}>
            {loading ? '…' : t('catalog.refresh')}
          </button>
        </div>
      </header>

      <div className="vc-admin-filters">
        {(['active', 'sold', 'all'] as StatusFilter[]).map((key) => (
          <button
            key={key}
            type="button"
            className={`vc-admin-chip${status === key ? ' is-active' : ''}`}
            onClick={() => setStatus(key)}
          >
            {t(`admin.listingsStatus.${key}`)}
          </button>
        ))}
        {(['', 'above_ai', 'no_photos', 'flagged', 'hidden'] as ListingFilter[]).map((key) => (
          <button
            key={key || 'all-filters'}
            type="button"
            className={`vc-admin-chip${filter === key ? ' is-active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {t(key ? `admin.listingsFilterTag.${key}` : 'admin.listingsFilterTag.all')}
          </button>
        ))}
      </div>

      <input
        type="search"
        className="vc-admin-input vc-admin-input--search"
        placeholder={t('admin.searchListings')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      {selectedIds.length > 0 ? (
        <div className="vc-admin-bulk-bar">
          <span>{t('admin.selectedCount', { count: selectedIds.length })}</span>
          <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={() => bulkPatch({ is_hidden: true })}>
            {t('admin.hideListing')}
          </button>
          <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={() => bulkPatch({ is_flagged_suspicious: true })}>
            {t('admin.flagListing')}
          </button>
        </div>
      ) : null}

      {error ? <p className="vc-admin-error">{error}</p> : null}
      {loading && items.length === 0 ? <AdminSkeleton variant="table" rows={6} /> : null}
      {!loading && items.length === 0 ? (
        <AdminEmptyState title={t('admin.noListings')} hint={t('admin.listingsIntro')} onAction={load} actionLabel={t('catalog.refresh')} />
      ) : null}

      {items.length > 0 ? (
        <>
        <div className="vc-admin-table-wrap">
          <table className="vc-admin-table vc-admin-table--clickable">
              <thead>
                <tr>
                  <th aria-label="Select" />
                  <th>{t('admin.listing')}</th>
                <th>{t('admin.owner')}</th>
                <th>{t('admin.amount')}</th>
                <th>{t('admin.photos')}</th>
                <th>{t('admin.status')}</th>
                <th>{t('admin.created')}</th>
              </tr>
            </thead>
            <tbody>
                {items.map((row) => (
                  <tr key={row.id} onClick={() => setSelectedId(row.id)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => toggleSelected(row.id)}
                        aria-label={`Select ${row.title}`}
                      />
                    </td>
                    <td>
                    <strong>
                      {row.manufacturer} {row.model_name}
                    </strong>
                    <div className="vc-admin-muted">{row.title}</div>
                  </td>
                  <td>{row.owner_email}</td>
                  <td>€{row.price.toFixed(0)}</td>
                  <td>{row.photos_count}</td>
                  <td>
                    {row.is_hidden ? <span className="vc-admin-badge vc-admin-badge--warn">{t('admin.hidden')}</span> : null}
                    {row.is_flagged_suspicious ? (
                      <span className="vc-admin-badge vc-admin-badge--warn">{t('admin.flagged')}</span>
                    ) : null}
                    {row.catalog_rank_group === 1 ? (
                      <span className="vc-admin-badge">{t('admin.aboveAiShort')}</span>
                    ) : null}
                  </td>
                  <td>{formatWhen(row.created_at, i18n.language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
        </>
      ) : null}

      {selectedId ? (
        <AdminListingDrawer
          token={token}
          listingId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={handleUpdated}
          onOpenUser={onOpenUser}
        />
      ) : null}
    </div>
  )
}
